import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "callaloo_cookie_consent_v1";

/**
 * Minimal consent banner. We only set strictly-necessary cookies (Clerk
 * session) and a single analytics-free preference flag, so the banner is
 * informational rather than a blocker. If we add tracking pixels we'll
 * gate them on `consent === 'accepted'`.
 */
export default function CookieBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setShow(true);
    } catch {
      // localStorage unavailable (private mode); skip the banner.
    }
  }, []);

  const dismiss = (value: "accepted" | "dismissed") => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* noop */
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm rounded-2xl border p-4 shadow-2xl z-50"
      style={{
        backgroundColor: "#1F2933",
        borderColor: "rgba(229,231,235,0.12)",
      }}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie notice"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
        >
          <Cookie size={16} style={{ color: "#F97316" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white">Cookies</p>
          <p className="mt-1 text-xs leading-relaxed" style={{ color: "#9CA3AF" }}>
            We use a single session cookie (set by Clerk) to keep you signed in. No tracking, no
            third-party advertising.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => dismiss("accepted")}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ backgroundColor: "#F97316" }}
            >
              Got it
            </button>
            <a
              href="https://www.callaloo.ai/privacy"
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-300 hover:text-white"
            >
              Privacy policy
            </a>
          </div>
        </div>
        <button
          onClick={() => dismiss("dismissed")}
          className="text-gray-500 hover:text-white p-1 rounded"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
